/* Amplitude Experiment (feature flags) config for Tokenwise (OPSS-55).
 *
 * Mirrors studio-web's Amplitude Experiment setup (there wrapped in the private
 * @ai21/experiments package): the raw @amplitude/experiment-js-client SDK,
 * pointed at a same-origin Caddy proxy so ad-blockers don't drop the calls, and
 * bound to the Amplitude Analytics instance the app already loads (shared
 * device/user id). Keyed on the presence of a deployment key rather than a
 * VITE_DEPLOYMENT_TYPE flag (which this app doesn't have) — same convention as
 * stigg.config.ts. No key ⇒ the noop flags service is used and every flag reads
 * off (the safe default for local/dev/on-prem). */

import { APP_BASENAME } from "../../auth/paths";

function readEnv(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;

  const value = window.__env__?.[key];
  return value && value.length > 0 ? value : undefined;
}

/** Amplitude Experiment client-side deployment key (values start with `client-`).
 *  Public by design — it only grants variant reads for the identified user. */
export const AMPLITUDE_EXPERIMENT_API_KEY = readEnv(
  "VITE_AMPLITUDE_EXPERIMENT_API_KEY",
);

/** Flags are Amplitude-backed only when a deployment key is configured. */
export const FLAGS_ENABLED = Boolean(AMPLITUDE_EXPERIMENT_API_KEY);

/** SDK debug logging on non-prod (matches studio-web: debug === dev env). */
export const FLAGS_DEBUG = readEnv("VITE_AMPLI_ENV") === "development";

/* Same-origin Caddy proxy paths for the Amplitude Experiment SDK. The app is
 * served at the host root (APP_BASENAME ""), so the proxy routes live at
 * /__/experiments/*; Caddy strips the prefix and forwards the SDK's own
 * sub-path to Amplitude. Built from window.location.origin so the URL follows
 * the current env's host with no per-env config (studio-web uses
 * VITE_APP_BASE_URL for the same purpose). */
function proxyUrl(suffix: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${APP_BASENAME}/__/experiments/${suffix}`;
}

/** serverUrl → Caddy → api.lab.amplitude.com (vardata fetch). */
export const AMPLITUDE_EXPERIMENT_SERVER_URL = proxyUrl("api");
/** flagsServerUrl → Caddy → flag.lab.amplitude.com (flag configs). */
export const AMPLITUDE_EXPERIMENT_FLAGS_SERVER_URL = proxyUrl("flags");

/** Backend Amplitude flag that decides whether a user is managed by Stigg
 *  (language-studio-api-server FeatureFlagNames.STIGG_IS_ENABLED_FOR_USER).
 *  On ⇒ the user is under Stigg, show billing UI; off/unknown ⇒ not managed,
 *  hide it — mirroring the backend, whose fallback for this flag is False. */
export const STIGG_ENABLED_FLAG = "stigg-enabled";

/** Amplitude flag gating the agent header's personalization level bar. Off
 *  until the Low/Medium/High thresholds in
 *  pages/agent/personalizationLevel.ts are settled — the meter makes a claim
 *  about sample size, so it shouldn't ship on numbers we're still tuning. */
export const PERSONALIZATION_LEVEL_FLAG = "ig-personalization-level";

/** Comma-separated flag keys forced ON where no Amplitude key is configured
 *  (local dev, on-prem). Debug affordance only: in any Amplitude-backed env the
 *  SaaS service answers and this is ignored, so it can never widen a rollout.
 *  Set it in .env.development.local, e.g.
 *  VITE_FLAGS_ON="ig-personalization-level". */
export const FLAGS_FORCED_ON: readonly string[] = (
  readEnv("VITE_FLAGS_ON") ?? ""
)
  .split(",")
  .map((k) => k.trim())
  .filter((k) => k.length > 0);

/** Amplitude flag gating email/password auth on the public login/sign-up
 *  screens — off ⇒ Google-only. Intended end state: served "on" only to a
 *  debugging cohort. Read anonymously (device-scoped) via
 *  useEmailPasswordAuth, since the auth screens render outside
 *  FlagsProvider/identity. */
export const EMAIL_PASSWORD_AUTH_FLAG = "igepa";

/** Amplitude flag gating the Harness Optimizer rollout (OPSS-379): the header
 *  app switcher here, and the /harness app itself on its side. Off/unknown ⇒ the
 *  switcher is hidden, so users can't reach an undeployed sibling app. */
export const HARNESS_OPTIMIZER_APP_FLAG = "ighoapp";
